import type { ReactNode } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";

import { AppShell } from "@/app/app-shell";
import { APP_ROUTES } from "@/app/navigation";
import { useAuthCheck } from "@/app/use-auth";
import { LoadingState } from "@/components/loading-state";
import { QueryErrorState } from "@/components/query-error-state";
import { FadeIn } from "@/components/ui/motion";
import { AnalysisDemoPage } from "@/pages/analysis-demo-page";
import { AnalyticsPage } from "@/pages/analytics-page";
import { CommandCenterPage } from "@/pages/command-center-page";
import { CostCenterPage } from "@/pages/cost-center-page";
import { CrossMarketPage } from "@/pages/cross-market-page";
import { DashboardPage } from "@/pages/dashboard-page";
import { FredEconomicPage } from "@/pages/fred-economic-page";
import { IntegrationsPage } from "@/pages/integrations-page";
import { LoginPage } from "@/pages/login-page";
import { ManifoldPage } from "@/pages/manifold-page";
import { MarketExplorerPage } from "@/pages/market-explorer-page";
import { NewsCenterPage } from "@/pages/news-center-page";
import { NotFoundPage } from "@/pages/not-found-page";
import { OpsPulsePage } from "@/pages/ops-pulse-page";
import { PredictitPage } from "@/pages/predictit-page";
import { PredictionEnginePage } from "@/pages/prediction-engine-page";
import { ReportsPage } from "@/pages/reports-page";
import { ResolutionCenterPage } from "@/pages/resolution-center-page";
import { SignalsPage } from "@/pages/signals-page";
import { TraderIntelligencePage } from "@/pages/trader-intelligence-page";
import { TwitterAnalysisPage } from "@/pages/twitter-analysis-page";
import { ValidationPage } from "@/pages/validation-page";
import { WorkbenchPage } from "@/pages/workbench-page";
import { PromptEvolutionPage } from "@/pages/prompt-evolution-page";
import { RemotePage } from "@/pages/remote-page";
import { TerminalPage } from "@/pages/terminal-page";
import { WorkflowAnalysisPage } from "@/pages/workflow-analysis-page";
import { YoutubeAnalysisPage } from "@/pages/youtube-analysis-page";
import { AgentLeaderboardPage, AlphaSignalsPage, DebateCouncilPage } from "@/features/alpha";
import { PortfolioPage } from "@/features/portfolio";

function renderRoutePage(routeKey: string): ReactNode {
  switch (routeKey) {
    case "dashboard":
      return <DashboardPage />;
    case "command-center":
      return <CommandCenterPage />;
    case "market-explorer":
      return <MarketExplorerPage />;
    case "prediction-engine":
      return <PredictionEnginePage />;
    case "signals":
      return <SignalsPage />;
    case "trader-intelligence":
      return <TraderIntelligencePage />;
    case "news-center":
      return <NewsCenterPage />;
    case "resolution-center":
      return <ResolutionCenterPage />;
    case "cross-market":
      return <CrossMarketPage />;
    case "ops-pulse":
      return <OpsPulsePage />;
    case "cost-center":
      return <CostCenterPage />;
    case "reports":
      return <ReportsPage />;
    case "integrations":
      return <IntegrationsPage />;
    case "advanced-analytics":
    case "analytics":
      return <AnalyticsPage />;
    case "analysis-demo":
      return <AnalysisDemoPage />;
    case "manifold":
      return <ManifoldPage />;
    case "predictit":
      return <PredictitPage />;
    case "fred-economic":
      return <FredEconomicPage />;
    case "twitter-analysis":
      return <TwitterAnalysisPage />;
    case "youtube-analysis":
      return <YoutubeAnalysisPage />;
    case "portfolio":
      return <PortfolioPage />;
    case "alpha-signals":
      return <AlphaSignalsPage />;
    case "alpha-debates":
      return <DebateCouncilPage />;
    case "alpha-leaderboard":
      return <AgentLeaderboardPage />;
    case "workflow-analysis":
      return <WorkflowAnalysisPage />;
    case "prompt-evolution":
      return <PromptEvolutionPage />;
    case "validation":
      return <ValidationPage />;
    default:
      return <WorkbenchPage routeKey={routeKey} />;
  }
}

function AnimatedPage({ children }: { children: ReactNode }) {
  return <FadeIn>{children}</FadeIn>;
}

export function ProtectedPage({
  routeKey,
  children,
}: {
  routeKey: string;
  children: ReactNode;
}) {
  const authQuery = useAuthCheck();

  if (authQuery.isLoading) {
    return (
      <div className="p-6">
        <LoadingState text="Checking session..." />
      </div>
    );
  }

  // A failed auth check with NO cached session means "backend unreachable",
  // not "logged out": /api/auth/check answers 200 {authenticated:false} for a
  // genuinely logged-out session, so the error path is network/5xx only.
  // With cached authenticated data, an errored background refetch must still
  // render the app (never hide the dashboard behind a retry screen).
  if (authQuery.isError && authQuery.data === undefined) {
    return (
      <div className="p-6">
        <QueryErrorState
          what="session status"
          onRetry={() => void authQuery.refetch()}
        />
      </div>
    );
  }

  if (!authQuery.data?.authenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell activeRouteKey={routeKey} username={authQuery.data.username}>
      {children}
    </AppShell>
  );
}

export function ProtectedTerminal({ children }: { children: ReactNode }) {
  const authQuery = useAuthCheck();

  if (authQuery.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadingState text="Checking session..." />
      </div>
    );
  }

  // Same backend-outage handling as ProtectedPage — this also guards /remote,
  // the emergency page itself, which must never bounce to a dead login form.
  if (authQuery.isError && authQuery.data === undefined) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <QueryErrorState
          what="session status"
          onRetry={() => void authQuery.refetch()}
        />
      </div>
    );
  }

  if (!authQuery.data?.authenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

const protectedRouteEntries = APP_ROUTES.flatMap((route) => {
  const paths = [route.canonicalPath, ...route.aliases];
  return paths.map((path) => ({
    path,
    element: <ProtectedPage routeKey={route.key}><AnimatedPage>{renderRoutePage(route.key)}</AnimatedPage></ProtectedPage>,
  }));
});

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/login.html", element: <LoginPage /> },
  { path: "/mobile/login.html", element: <LoginPage /> },
  // Dashboard is the default landing page; its "/" registration comes from the
  // APP_ROUTES expansion below (dashboard.canonicalPath === "/") — do not add
  // a second explicit "/" entry here.
  // Terminal (full-screen, no AppShell)
  { path: "/terminal", element: <ProtectedTerminal><TerminalPage /></ProtectedTerminal> },
  // Mobile remote (minimal, emergency controls)
  { path: "/remote", element: <ProtectedTerminal><RemotePage /></ProtectedTerminal> },
  // Redirects
  { path: "/markets", element: <Navigate to="/market-explorer" replace /> },
  { path: "/settings", element: <Navigate to="/" replace /> },
  // APP_ROUTES expansion: every sidebar-nav route plus its .html aliases
  // (includes "/" via dashboard.canonicalPath).
  ...protectedRouteEntries,
  // Unknown paths get an explicit 404 (inside the shell) instead of a silent
  // dashboard redirect that hides broken/stale deep links.
  { path: "*", element: <ProtectedPage routeKey="not-found"><AnimatedPage><NotFoundPage /></AnimatedPage></ProtectedPage> },
]);
